import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { Link } from "react-router-dom";

type DashboardProps = {
  data?: Record<string, any>;
};

const numberValue = (...values: any[]) => {
  const value = values.find((item) => item !== undefined && item !== null && item !== "");
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const money = (value: any) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(numberValue(value));

const listValue = (data: Record<string, any>, keys: string[]) => {
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }
  return [];
};

const FinanceDashboardOverview: React.FC<DashboardProps> = ({ data = {} }) => {
  const monthlyDisbursement = listValue(data, [
    "monthlyDisbursement",
    "monthlyDisbursements",
    "disbursementByMonth",
  ]);
  const monthlyPayments = listValue(data, [
    "monthlyPayments",
    "monthlyCollection",
    "paymentByMonth",
  ]);
  const loanStatus = listValue(data, ["loansByStatus", "loanByStatus", "loanStatus"]);
  const upcomingEmis = listValue(data, ["dailyEmiCollection", "upcomingEmis", "dailyEMIDetails"]);
  const overdueRepayment = listValue(data, ["overdueRepayment", "overdueBuckets"]);
  const customers = listValue(data, ["currentTenCustomers", "recentCustomers", "customers"]).slice(0, 10);

  const cards = [
    { label: "Branches", value: numberValue(data.branchCount, data.retailerCount), icon: "ti-building-store", tone: "cyan" },
    { label: "Employees", value: numberValue(data.employeeCount, data.employeeData), icon: "ti-users", tone: "rose" },
    { label: "Customers", value: numberValue(data.totalCustomer, data.customerCount?.yearly, data.customerCount), icon: "ti-user-check", tone: "green", link: "/customers" },
    { label: "Total Loans", value: numberValue(data.totalLoans), icon: "ti-file-invoice", tone: "blue" },
    { label: "Total Disbursement", value: money(data.totalDisbursementAmount), icon: "ti-cash-banknote", tone: "orange" },
    { label: "Total EMI Amount", value: money(data.totalEmiAmount), icon: "ti-coins", tone: "indigo" },
    { label: "Pending EMI Amount", value: money(data.pendingEmiAmountTotal), icon: "ti-calendar-dollar", tone: "amber" },
    { label: "EMI Payments Received", value: money(data.totalPayment), icon: "ti-receipt-rupee", tone: "emerald" },
    { label: "Total Interest", value: money(data.interestTotalAmount), icon: "ti-percentage", tone: "pink" },
    { label: "Loan Products", value: numberValue(data.productCount, data.interestCount), icon: "ti-packages", tone: "purple" },
    { label: "Today's Collection", value: money(data.todayCollection), icon: "ti-wallet", tone: "teal" },
    { label: "Overdue EMIs", value: numberValue(data.overdueEmis), icon: "ti-alert-triangle", tone: "rose" },
  ];

  const summaryCards = [
    { label: "Total Collections", value: money(data.totalPayment || data.paymentsData?.totalAmount), detail: "All received EMI payments" },
    { label: "This Month's Collection", value: money(data.currentMonthPaymentSum), detail: "Current month performance" },
    { label: "Outstanding Portfolio", value: money(data.pendingEmiAmountTotal), detail: "Pending EMI receivables" },
  ];

  const chartBase: any = {
    chart: { toolbar: { show: false }, fontFamily: "inherit" },
    dataLabels: { enabled: false },
    grid: { borderColor: "#eef0f5", strokeDashArray: 4 },
    colors: ["#5b5bd6", "#22a06b", "#f59e0b", "#ef5da8"],
    noData: { text: "No dashboard data available", align: "center" as const },
  };

  const chartData = useMemo(() => {
    const monthLabel = (item: any) => item.label || item.monthName || item.month || item.date || "";
    return {
      disbursementCategories: monthlyDisbursement.map(monthLabel),
      disbursementValues: monthlyDisbursement.map((item: any) =>
        numberValue(item.totalDisbursed, item.totalDisbursement, item.amount, item.value),
      ),
      paymentCategories: monthlyPayments.map(monthLabel),
      paymentValues: monthlyPayments.map((item: any) =>
        numberValue(item.totalPayments, item.totalPayment, item.amount, item.value),
      ),
      statusLabels: loanStatus.map((item: any) => item.status || item.label || "Unknown"),
      statusValues: loanStatus.map((item: any) => numberValue(item.count, item.value)),
      emiCategories: upcomingEmis.map((item: any) => item.date || item.label || ""),
      emiValues: upcomingEmis.map((item: any) =>
        numberValue(item.totalPendingEmis, item.pendingAmount, item.amount, item.value),
      ),
      overdueCategories: overdueRepayment.map((item: any) => item.label || item.bucket || ""),
      overdueValues: overdueRepayment.map((item: any) => numberValue(item.amount, item.value)),
    };
  }, [monthlyDisbursement, monthlyPayments, loanStatus, upcomingEmis, overdueRepayment]);
  const repaymentTotal = chartData.paymentValues.reduce((sum, value) => sum + numberValue(value), 0);

  return (
    <section className="mf-overview" aria-label="Microfinance dashboard overview">
      <div className="mf-chart-grid">
        <article className="mf-panel mf-chart-main">
          <div className="mf-panel-title"><h3>Monthly Loan Disbursement (Last 12 Months)</h3></div>
          <ReactApexChart
            options={{
              ...chartBase,
              chart: { ...chartBase.chart, toolbar: { show: true } },
              colors: ["#2196f3"],
              dataLabels: { enabled: true, style: { colors: ["#ffffff"], fontSize: "10px" }, formatter: (value: number) => value ? Math.round(value).toLocaleString("en-IN") : "" },
              xaxis: { categories: chartData.disbursementCategories, labels: { rotate: 0, style: { fontSize: "10px" } } },
              yaxis: { labels: { formatter: money, style: { fontSize: "10px" } } },
              plotOptions: { bar: { borderRadius: 0, columnWidth: "62%" } },
            }}
            series={[{ name: "Loan disbursed", data: chartData.disbursementValues }]}
            type="bar"
            height={260}
          />
        </article>
        <article className="mf-panel mf-chart-large">
          <div className="mf-panel-title">
            <h3>Monthly Repayment Trend (Last 6 Months)</h3>
            <strong className="mf-panel-total">{money(repaymentTotal)}</strong>
          </div>
          {chartData.paymentValues.some((value) => value > 0) ? (
            <ReactApexChart
              key={`repayment-${chartData.paymentValues.join("-")}`}
              options={{
                ...chartBase,
                chart: { type: "area", height: 250, toolbar: { show: false }, zoom: { enabled: false }, fontFamily: "inherit" },
                colors: ["#2196f3"],
                xaxis: {
                  categories: chartData.paymentCategories,
                  axisBorder: { show: false },
                  axisTicks: { show: false },
                  labels: { style: { fontSize: "10px" } },
                },
                yaxis: { min: 0, labels: { formatter: money, style: { fontSize: "10px" } } },
                stroke: { curve: "straight", width: 4, colors: ["#2196f3"] },
                markers: { size: 4, colors: ["#2196f3"], strokeColors: "#ffffff", strokeWidth: 2 },
                fill: { type: "solid", opacity: 0.22, colors: ["#2196f3"] },
                dataLabels: {
                  enabled: true,
                  offsetY: -7,
                  formatter: (value: number) => Math.round(numberValue(value)).toLocaleString("en-IN"),
                  style: { colors: ["#1769aa"], fontSize: "9px", fontWeight: 700 },
                  background: { enabled: true, foreColor: "#ffffff", borderRadius: 2, padding: 3, opacity: 0.9 },
                },
                tooltip: { y: { formatter: money } },
              }}
              series={[{ name: "Repayment", data: chartData.paymentValues }]}
              type="area"
              height={250}
            />
          ) : (
            <div className="mf-empty-chart">No repayment data available for the last 6 months</div>
          )}
        </article>
        <article className="mf-panel mf-chart-small">
          <div className="mf-panel-title"><h3>Loan Status Distribution</h3></div>
          <ReactApexChart options={{ ...chartBase, chart: { ...chartBase.chart, toolbar: { show: false } }, labels: chartData.statusLabels, colors: ["#2196f3", "#20c997", "#ffad16", "#ed3446", "#6f42c1"], dataLabels: { enabled: true, formatter: (value: number) => `${value.toFixed(1)}%`, style: { fontSize: "10px" } }, legend: { position: "bottom", fontSize: "10px", markers: { size: 5 } }, plotOptions: { pie: { donut: { size: "64%" } } } }} series={chartData.statusValues} type="donut" height={250} />
        </article>
        <article className="mf-panel mf-chart-large">
          <div className="mf-panel-title"><h3>Upcoming 20 Days EMI Collection</h3></div>
          <ReactApexChart options={{ ...chartBase, chart: { ...chartBase.chart, toolbar: { show: true } }, colors: ["#2196f3"], xaxis: { categories: chartData.emiCategories, labels: { rotate: -50, style: { fontSize: "9px" } } }, yaxis: { labels: { formatter: money, style: { fontSize: "10px" } } }, stroke: { curve: "straight", width: 3 }, markers: { size: 3 }, dataLabels: { enabled: true, background: { enabled: true, borderRadius: 2, padding: 3 }, style: { fontSize: "9px" }, formatter: (value: number) => value ? Math.round(value).toString() : "" }, fill: { type: "solid", opacity: 0.28 } }} series={[{ name: "Pending EMIs", data: chartData.emiValues }]} type="area" height={250} />
        </article>
        <article className="mf-panel mf-chart-small">
          <div className="mf-panel-title"><h3>Overdue Repayment</h3></div>
          <ReactApexChart options={{ ...chartBase, chart: { ...chartBase.chart, toolbar: { show: false } }, labels: chartData.overdueCategories, colors: ["#ffad16", "#ed3446", "#6f42c1", "#26334d"], dataLabels: { enabled: true, formatter: (value: number) => `${value.toFixed(1)}%`, style: { fontSize: "10px" } }, legend: { position: "bottom", fontSize: "10px", markers: { size: 5 } }, plotOptions: { pie: { donut: { size: "64%" } } } }} series={chartData.overdueValues} type="donut" height={250} />
        </article>
      </div>

      <div className="mf-summary-grid">
        {summaryCards.map((item) => (
          <article className="mf-summary-card" key={item.label}>
            <p>{item.label}</p><strong>{item.value}</strong><small>{item.detail}</small>
          </article>
        ))}
      </div>

      <div className="mf-kpi-grid">
        {cards.map((item) => (
          <article className="mf-kpi-card" key={item.label}>
            <span className={`mf-kpi-icon mf-tone-${item.tone}`}><i className={`ti ${item.icon}`} /></span>
            <div><p>{item.label}</p><strong>{item.value}</strong></div>
            {item.link && <Link to={item.link} aria-label={`View ${item.label}`}><i className="ti ti-arrow-up-right" /></Link>}
          </article>
        ))}
      </div>

      <article className="mf-panel mf-customer-panel">
        <div className="mf-panel-title"><div><span>Customers</span><h3>Recent customers</h3></div><Link to="/customers">View all <i className="ti ti-arrow-right" /></Link></div>
        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead><tr><th>Customer</th><th>Contact</th><th>Loan account</th><th>Loan amount</th><th>Status</th></tr></thead>
            <tbody>
              {customers.length ? customers.map((customer: any, index: number) => (
                <tr key={customer.id || index}>
                  <td><div className="mf-customer"><span>{String(customer.full_name || customer.name || "C").charAt(0).toUpperCase()}</span><div><strong>{customer.full_name || customer.name || "Customer"}</strong><small>{customer.createdAt ? new Date(customer.createdAt).toLocaleDateString("en-IN") : "Recently added"}</small></div></div></td>
                  <td>{customer.phoneNumber1 || customer.phone || customer.mobile || "--"}</td>
                  <td>{customer.loanAccountNumber || customer.accountNumber || "--"}</td>
                  <td>{money(customer.loanAmount || customer.disbursementAmount || customer.amount)}</td>
                  <td><span className="mf-status">{customer.status || customer.loanStatus || "Active"}</span></td>
                </tr>
              )) : <tr><td colSpan={5} className="mf-empty">No recent customers available</td></tr>}
            </tbody>
          </table>
        </div>
      </article>
    </section>
  );
};

export default FinanceDashboardOverview;
